// FilterDropdown — two-pane category filter popover. A trigger button (icon +
// label + applied-count pill + chevron) opens a Dropdown panel with a left rail
// of categories and a right pane holding an opt-in search box and the active
// category's checkbox options. Edits accumulate in an internal DRAFT — badges
// inside the panel track the draft live, but the caller's value only changes
// when Done fires onApply; Escape/outside-click discards the draft (it is
// re-seeded from `value` on every open). Clear all empties the draft only.
// Categories are multi-select checkbox lists by default; type:'single' renders
// radio-style icon rows instead (nothing selected until picked, no unselect),
// and its rangeOptionId option reveals FROM/TO date fields whose values travel
// in the separate `ranges` record (drafted and applied alongside the sets).
// The footer copy is fixed and resolved internally (button_done /
// button_clear_all) — the one deliberate exception to the caller-passes-labels
// norm in common/.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import Checkbox from './Checkbox';
import Dropdown from './Dropdown';
import Icon from './Icon';
import Input from './Input';

export interface FilterDropdownOption {
  id: string;
  label: string;
  /** Leading icon on type:'single' rows — replaced by a check when selected. */
  icon?: string;
  /** Optional leading status dot — a CSS color; semantic tokens preferred,
   *  e.g. 'var(--success)'. The dot keeps this color when the row is checked
   *  (the label turns brand). */
  dotColor?: string;
  /** Render the row disabled (e.g. a locked/always-on option). */
  disabled?: boolean;
}

/** FROM/TO values of a range category (yyyy-mm-dd, '' = unset). */
export interface FilterDropdownDateRange {
  start: string;
  end: string;
}

export interface FilterDropdownCategory {
  key: string;
  label: string;
  /** Lucide kebab-case icon for the rail row, e.g. 'circle-dot', 'flag'. */
  icon: string;
  options: FilterDropdownOption[];
  /** 'multi' (default) = checkbox list; 'single' = radio-style icon rows. */
  type?: 'multi' | 'single';
  /** type:'single' only — while this option id is selected, FROM/TO date
   *  fields render below the options (values ride in `ranges`). */
  rangeOptionId?: string;
  /** type:'single' only — the "no filter" option (e.g. "Any time" backed by
   *  undefined on the wire): renders as selected while the category's set is
   *  empty, never counts toward badges, and picking it clears the category. */
  defaultOptionId?: string;
  /** Opt-in search box over this category's options (client-side, multi only). */
  searchable?: boolean;
  searchPlaceholder?: string;
}

export interface FilterDropdownProps {
  /** Trigger label, also the accessible name. */
  label: string;
  categories: FilterDropdownCategory[];
  /** APPLIED selection: categoryKey -> set of selected option ids. Caller-owned. */
  value: Record<string, Set<string>>;
  /** APPLIED range values per category key (categories with rangeOptionId). */
  ranges?: Record<string, FilterDropdownDateRange>;
  /** Fired on Done with the full drafts (every category key present). */
  onApply: (
    next: Record<string, Set<string>>,
    ranges: Record<string, FilterDropdownDateRange>,
  ) => void;
  emptyText?: string;
  /** Trigger icon. Default 'sliders-horizontal'. */
  icon?: string;
  /** Panel edge to align with the trigger. Default 'start' (toolbar left). */
  align?: 'start' | 'end';
  width?: number;
  /** Render the panel in a body portal to escape a clipping ancestor. */
  portal?: boolean;
  /** Portal only — open above the trigger when the panel would run past the
   *  viewport bottom (fixed positioning can't be scrolled into view). */
  flip?: boolean;
}

const seedDraft = (categories: FilterDropdownCategory[], value: Record<string, Set<string>>) =>
  Object.fromEntries(categories.map((c) => [c.key, new Set(value[c.key] ?? [])]));

const FilterDropdown = ({
  label,
  categories,
  value,
  ranges,
  onApply,
  emptyText,
  icon = 'sliders-horizontal',
  align = 'start',
  width = 600,
  portal = false,
  flip = false,
}: FilterDropdownProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [rangeDraft, setRangeDraft] = useState<Record<string, FilterDropdownDateRange>>({});
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const appliedCount = useMemo(() => Object.values(value).reduce((n, s) => n + s.size, 0), [value]);
  const draftCount = Object.values(draft).reduce((n, s) => n + s.size, 0);

  // Guard against `categories` changing while open: fall back to the first.
  const active = categories.find((c) => c.key === activeKey) ?? categories[0];
  const picked = active ? (draft[active.key] ?? new Set<string>()) : new Set<string>();

  const trimmed = query.trim().toLowerCase();
  const visibleOptions =
    active?.searchable && trimmed
      ? active.options.filter((o) => o.label.toLowerCase().includes(trimmed))
      : (active?.options ?? []);

  const openPanel = () => {
    setDraft(seedDraft(categories, value));
    setRangeDraft({ ...ranges });
    setActiveKey(categories[0]?.key ?? null);
    setQuery('');
    setOpen(true);
  };

  const toggle = (categoryKey: string, id: string) =>
    setDraft((prev) => {
      const next = new Set(prev[categoryKey]);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, [categoryKey]: next };
    });

  // Radio semantics: picking replaces the (0..1-sized) set; no unselect. The
  // category's defaultOptionId means "no filter", so picking it clears instead.
  const pick = (category: FilterDropdownCategory, id: string) =>
    setDraft((prev) => ({
      ...prev,
      [category.key]: id === category.defaultOptionId ? new Set<string>() : new Set([id]),
    }));

  const patchRange = (categoryKey: string, patch: Partial<FilterDropdownDateRange>) =>
    setRangeDraft((prev) => ({
      ...prev,
      [categoryKey]: { start: '', end: '', ...prev[categoryKey], ...patch },
    }));

  const clearAll = () => {
    setDraft(Object.fromEntries(categories.map((c) => [c.key, new Set<string>()])));
    setRangeDraft({});
  };

  const apply = () => {
    onApply(draft, rangeDraft);
    setOpen(false);
  };

  const triggerClass = ['filter-dropdown__trigger', (open || appliedCount > 0) && 'is-open']
    .filter(Boolean)
    .join(' ');

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align={align}
      width={width}
      portal={portal}
      flip={flip}
      className='filter-dropdown'
      trigger={
        <button
          type='button'
          className={triggerClass}
          aria-expanded={open}
          onClick={() => (open ? setOpen(false) : openPanel())}
        >
          <Icon name={icon} size={16} />
          {label}
          {appliedCount > 0 && <span className='filter-dropdown__count'>{appliedCount}</span>}
          <Icon
            name={open ? 'chevron-up' : 'chevron-down'}
            size={16}
            className='filter-dropdown__caret'
          />
        </button>
      }
    >
      <div className='filter-dropdown__panel'>
        <div className='filter-dropdown__body'>
          <div className='filter-dropdown__rail'>
            {categories.map((c) => {
              const count = draft[c.key]?.size ?? 0;
              const catClass = ['filter-dropdown__cat', c.key === active?.key && 'is-active']
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  key={c.key}
                  type='button'
                  className={catClass}
                  aria-pressed={c.key === active?.key}
                  onClick={() => {
                    setActiveKey(c.key);
                    setQuery('');
                  }}
                >
                  <Icon name={c.icon} size={16} />
                  <span className='filter-dropdown__cat-label'>{c.label}</span>
                  {count > 0 && <span className='filter-dropdown__cat-count'>{count}</span>}
                </button>
              );
            })}
          </div>
          <div className='filter-dropdown__main'>
            {active?.type === 'single' ? (
              <>
                <div className='filter-dropdown__list'>
                  {active.options.map((o) => {
                    const isPicked =
                      picked.has(o.id) || (picked.size === 0 && o.id === active.defaultOptionId);
                    const radioClass = ['filter-dropdown__radio', isPicked && 'is-selected']
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <button
                        key={o.id}
                        type='button'
                        role='menuitemradio'
                        aria-checked={isPicked}
                        className={radioClass}
                        disabled={o.disabled}
                        onClick={() => pick(active, o.id)}
                      >
                        <Icon name={isPicked ? 'check' : (o.icon ?? 'circle')} size={16} />
                        {o.label}
                      </button>
                    );
                  })}
                  {active.options.length === 0 && (
                    <span className='filter-dropdown__empty'>{emptyText}</span>
                  )}
                </div>
                {active.rangeOptionId && picked.has(active.rangeOptionId) && (
                  <div className='filter-dropdown__range'>
                    <label className='filter-dropdown__range-field'>
                      <span className='filter-dropdown__range-label'>{t('text_from')}</span>
                      <Input
                        type='date'
                        size='sm'
                        value={rangeDraft[active.key]?.start ?? ''}
                        onChange={(e) => patchRange(active.key, { start: e.target.value })}
                      />
                    </label>
                    <label className='filter-dropdown__range-field'>
                      <span className='filter-dropdown__range-label'>{t('text_to')}</span>
                      <Input
                        type='date'
                        size='sm'
                        value={rangeDraft[active.key]?.end ?? ''}
                        onChange={(e) => patchRange(active.key, { end: e.target.value })}
                      />
                    </label>
                  </div>
                )}
              </>
            ) : (
              <>
                {active?.searchable && (
                  <Input
                    size='sm'
                    iconStart='search'
                    placeholder={active.searchPlaceholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                )}
                <div className='filter-dropdown__list'>
                  {visibleOptions.map((o) => (
                    <Checkbox
                      key={o.id}
                      className='filter-dropdown__opt'
                      label={
                        o.dotColor ? (
                          <>
                            <span
                              className='filter-dropdown__opt-dot'
                              style={{ backgroundColor: o.dotColor }}
                              aria-hidden='true'
                            />
                            {o.label}
                          </>
                        ) : (
                          o.label
                        )
                      }
                      checked={picked.has(o.id)}
                      disabled={o.disabled}
                      onChange={() => active && toggle(active.key, o.id)}
                      size='sm'
                    />
                  ))}
                  {visibleOptions.length === 0 && (
                    <span className='filter-dropdown__empty'>{emptyText}</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className='filter-dropdown__footer'>
          {draftCount > 0 && (
            <Button variant='secondary' size='lg' iconStart='x' onClick={clearAll}>
              {t('button_clear_all')}
            </Button>
          )}
          <Button variant='primary' size='lg' iconStart='check' onClick={apply}>
            {t('button_done')}
          </Button>
        </div>
      </div>
    </Dropdown>
  );
};

export default FilterDropdown;
